import { useEffect, useRef, useState } from 'react';
import { buscarFotos, traerFotoSugerida, type Busqueda } from '../lib/fotosSugeridas.js';
import { useT } from '../i18n/index.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';

/** Photos whose bytes are asked for at a time: the sheet fills quickly, the shop's server is small. */
const A_LA_VEZ = 3;

/**
 * «Fotos de internet»: what the shop's server found for a product, to pick one for it (the owner,
 * 2026-10-22: «a photo suggester that looks for photos on the internet of that particular product as
 * a suggested picture we can use for that product's thumbnail»).
 *
 * The pictures in a grid, white behind them as the photo will have it, the one found by the
 * product's own code marked as such; one tapped is chosen, and where it was found is said under them
 * with a way to its page. «Usar esta foto» hands its bytes back; the screen makes it the product's
 * photo, whole. A picture that does not arrive is left out. With no connection, nothing found, or a
 * place that could not be asked just now, the sheet says so, with «Buscar otra vez» where another
 * try could find something.
 */
export function FotosSugeridas({
  producto,
  nombre,
  codigos,
  onUsar,
  onCerrar,
}: {
  /** The product as the screen names it. */
  producto: string;
  /** The shop's own name for it, which is what is searched. */
  nombre: string;
  codigos: readonly string[];
  onUsar: (foto: Blob) => void;
  onCerrar: () => void;
}) {
  const t = useT();
  const [vez, setVez] = useState(0);
  const [busqueda, setBusqueda] = useState<Busqueda | null>(null);
  /** Each photo once its bytes are here, by id: its address on this page, or null for one that did not come. */
  const [imagenes, setImagenes] = useState<ReadonlyMap<string, { url: string; blob: Blob } | null>>(() => new Map());
  const [elegida, setElegida] = useState<string | null>(null);
  /** The pictures' addresses on this page, let go with the sheet. */
  const urls = useRef<string[]>([]);
  const deLosCodigos = codigos.join(',');

  useEffect(() => {
    let vigente = true;
    setBusqueda(null);
    void buscarFotos(nombre, deLosCodigos ? deLosCodigos.split(',') : []).then((b) => {
      if (vigente) setBusqueda(b);
    });
    return () => {
      vigente = false;
    };
  }, [nombre, deLosCodigos, vez]);

  const fotos = busqueda?.estado === 'listo' ? busqueda.fotos : null;
  useEffect(() => {
    if (!fotos) return;
    let vigente = true;
    const cola = fotos.map((f) => f.id);
    void Promise.all(
      Array.from({ length: A_LA_VEZ }, async () => {
        for (let id = cola.shift(); id !== undefined; id = cola.shift()) {
          const blob = await traerFotoSugerida(id);
          if (!vigente) return;
          const url = blob ? URL.createObjectURL(blob) : null;
          if (url) urls.current.push(url);
          setImagenes((antes) => new Map(antes).set(id, blob && url ? { url, blob } : null));
        }
      }),
    );
    return () => {
      vigente = false;
    };
  }, [fotos]);
  useEffect(
    () => () => {
      for (const url of urls.current) URL.revokeObjectURL(url);
    },
    [],
  );

  /** Those whose bytes came, or are still coming. */
  const visibles = fotos?.filter((f) => imagenes.get(f.id) !== null) ?? [];
  const deLaElegida = fotos?.find((f) => f.id === elegida);
  const bytes = elegida ? imagenes.get(elegida) : undefined;
  const otraVez = () => {
    setElegida(null);
    setVez((v) => v + 1);
  };

  return (
    <Modal
      titulo={t('sugeridas.titulo')}
      onCerrar={onCerrar}
      pie={
        visibles.length > 0 && (
          <button type="button" className="btn-primario w-full" disabled={!bytes} onClick={() => bytes && onUsar(bytes.blob)} data-sugeridas-usar>
            <Ico icono="confirmar" />
            {t('sugeridas.usar')}
          </button>
        )
      }
    >
      <p className="mb-4 text-sm text-slate-400">{t('sugeridas.explicacion', { producto })}</p>
      {busqueda === null ? (
        <p role="status" className="flex items-center gap-2 py-6 text-sm text-slate-300" data-sugeridas-estado="buscando">
          <Ico icono="internet" className="size-4 shrink-0" />
          {t('sugeridas.buscando')}
        </p>
      ) : busqueda.estado !== 'listo' ? (
        <Aviso texto={t(busqueda.estado === 'sinConexion' ? 'sugeridas.sinConexion' : 'sugeridas.error')} estado={busqueda.estado} onOtraVez={otraVez} />
      ) : busqueda.fotos.length === 0 ? (
        <Aviso
          texto={t(busqueda.ocupado ? 'sugeridas.ocupado' : 'sugeridas.ninguna')}
          estado={busqueda.ocupado ? 'ocupado' : 'ninguna'}
          {...(busqueda.ocupado ? { onOtraVez: otraVez } : {})}
        />
      ) : visibles.length === 0 ? (
        <Aviso texto={t('sugeridas.noLlegaron')} estado="noLlegaron" onOtraVez={otraVez} />
      ) : (
        <>
          <ul className="grid grid-cols-3 gap-3" data-sugeridas>
            {visibles.map((f) => {
              const imagen = imagenes.get(f.id);
              const esta = f.id === elegida;
              return (
                <li key={f.id} className="min-w-0">
                  <button
                    type="button"
                    onClick={() => setElegida(f.id)}
                    aria-pressed={esta}
                    aria-label={t('sugeridas.elegir', { fuente: f.fuente, titulo: f.titulo || f.fuente })}
                    data-sugerida={f.id}
                    data-sugerida-exacta={f.exacta ? '' : undefined}
                    data-sin-icono="fila"
                    className={`relative block aspect-square w-full overflow-hidden rounded-xl bg-white transition ${
                      esta ? 'ring-2 ring-marca-500 ring-offset-2 ring-offset-slate-900' : 'hover:ring-2 hover:ring-white/40'
                    }`}
                  >
                    {imagen ? (
                      <img src={imagen.url} alt="" className="size-full object-contain" />
                    ) : (
                      <span className="flex size-full items-center justify-center text-slate-300">
                        <Ico icono="internet" className="size-6" />
                      </span>
                    )}
                    {f.exacta && (
                      <span className="absolute top-1 left-1 rounded-md bg-teal-700 px-1.5 py-0.5 text-[10px] leading-tight font-semibold text-white">
                        {t('sugeridas.porSuCodigo')}
                      </span>
                    )}
                    {esta && (
                      <span className="absolute right-1 bottom-1 flex size-6 items-center justify-center rounded-full bg-marca-600 text-white">
                        <Ico icono="marca" className="size-4" />
                      </span>
                    )}
                  </button>
                  <p className="mt-1 truncate text-[11px] text-slate-500">{f.fuente}</p>
                </li>
              );
            })}
          </ul>
          {deLaElegida && (
            <p className="mt-4 text-xs wrap-break-word text-slate-400" data-sugerida-credito>
              {t('sugeridas.de', { fuente: deLaElegida.fuente })}
              {deLaElegida.titulo ? `: ${deLaElegida.titulo}` : ''}{' '}
              <a href={deLaElegida.enlace} target="_blank" rel="noreferrer" className="btn-texto inline-flex gap-1 text-teal-300">
                <Ico icono="afuera" className="size-3.5" />
                {t('sugeridas.verPagina')}
              </a>
            </p>
          )}
          {busqueda.ocupado && (
            <p className="mt-3 text-xs text-amber-300" data-sugeridas-estado="ocupado">
              {t('sugeridas.ocupadoAlgo')}{' '}
              <button type="button" className="btn-texto inline-flex gap-1 text-amber-200" onClick={otraVez}>
                <Ico icono="reiniciar" className="size-3.5" />
                {t('sugeridas.otraVez')}
              </button>
            </p>
          )}
        </>
      )}
    </Modal>
  );
}

/** What there is to say instead of photos, and «Buscar otra vez» where another try could find some. */
function Aviso({ texto, estado, onOtraVez }: { texto: string; estado: string; onOtraVez?: () => void }) {
  const t = useT();
  return (
    <div className="flex flex-col items-start gap-3 py-4" data-sugeridas-estado={estado}>
      <p className="text-sm text-slate-300">{texto}</p>
      {onOtraVez && (
        <button type="button" className="btn-secundario px-4 text-sm" onClick={onOtraVez}>
          <Ico icono="reiniciar" className="size-4" />
          {t('sugeridas.otraVez')}
        </button>
      )}
    </div>
  );
}
